export interface DraftRow {
  id: string;
  number: string;
  name: string;
  accountType: string;
  detailType: string;
}

export type RowStatus = 'ready' | 'empty' | 'error';

export interface ValidatedRow extends DraftRow {
  status: RowStatus;
  errorMessage?: string;
}

export type DraftField = 'number' | 'name' | 'accountType' | 'detailType';

const COL_ORDER: DraftField[] = ['number', 'name', 'accountType', 'detailType'];

export function createEmptyRow(): DraftRow {
  return {
    id: `d-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    number: '',
    name: '',
    accountType: '',
    detailType: '',
  };
}

export function isRowEmpty(row: DraftRow): boolean {
  return !row.number && !row.name && !row.accountType && !row.detailType;
}

export function validateRows(rows: DraftRow[], existingAccountNames: string[], validAccountTypes: string[]): ValidatedRow[] {
  const existingSet = new Set(existingAccountNames.map((n) => n.trim().toLowerCase()));
  const seen = new Set<string>();
  return rows.map((row) => {
    if (isRowEmpty(row)) return { ...row, status: 'empty' as const };
    const name = row.name.trim();
    const accountType = row.accountType.trim();
    if (!name) return { ...row, status: 'error' as const, errorMessage: 'Account name is required.' };
    if (!accountType) return { ...row, status: 'error' as const, errorMessage: 'Account type is required.' };
    if (!validAccountTypes.includes(accountType)) {
      return { ...row, status: 'error' as const, errorMessage: `"${accountType}" is not a valid Account type.` };
    }
    const nameLower = name.toLowerCase();
    if (existingSet.has(nameLower)) {
      return { ...row, status: 'error' as const, errorMessage: 'Already exists in your Chart of accounts.' };
    }
    if (seen.has(nameLower)) {
      return { ...row, status: 'error' as const, errorMessage: 'Duplicate in this batch.' };
    }
    seen.add(nameLower);
    return { ...row, status: 'ready' as const };
  });
}

export function parsePastedGrid(text: string): string[][] {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  return lines.map((line) => {
    const sep = line.includes('\t') ? '\t' : ',';
    return line.split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''));
  });
}

export function applyPastedGrid(rows: DraftRow[], startRowIdx: number, startCol: DraftField, pasted: string[][]): DraftRow[] {
  const next = [...rows];
  const startColIdx = COL_ORDER.indexOf(startCol);
  pasted.forEach((rowValues, rowOffset) => {
    const targetIdx = startRowIdx + rowOffset;
    while (next.length <= targetIdx) next.push(createEmptyRow());
    rowValues.forEach((value, colOffset) => {
      const targetColIdx = startColIdx + colOffset;
      if (targetColIdx < COL_ORDER.length) {
        next[targetIdx] = { ...next[targetIdx], [COL_ORDER[targetColIdx]]: value };
      }
    });
  });
  return next;
}

export function useCreateMultipleAccounts() {
  return { createEmptyRow, isRowEmpty, validateRows, parsePastedGrid, applyPastedGrid };
}